import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Trash2, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ withCredentials: true });

const Stars = ({ n }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`w-4 h-4 ${i <= n ? "text-gold fill-gold" : "text-smoke/30"}`} />
    ))}
  </div>
);

export default function ReviewsManager() {
  const [items, setItems] = useState(null);

  const load = () =>
    http.get(`${API}/admin/reviews`)
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Erro ao carregar avaliações."));

  useEffect(() => { load(); }, []);

  const toggle = async (id, featured) => {
    try {
      await http.patch(`${API}/admin/reviews/${id}`, { featured });
      toast.success(featured ? "Avaliação em destaque no site." : "Destaque removido.");
      load();
    } catch {
      toast.error("Erro ao atualizar.");
    }
  };

  const del = async (id) => {
    try {
      await http.delete(`${API}/admin/reviews/${id}`);
      toast.success("Avaliação removida.");
      setItems((prev) => prev.filter((r) => r.id !== id));
    } catch {
      toast.error("Erro ao remover.");
    }
  };

  if (!items) {
    return <p className="py-16 text-center text-sm text-smoke" data-testid="reviews-loading">Carregando...</p>;
  }

  return (
    <main className="max-w-6xl mx-auto px-5 md:px-8 py-10" data-testid="reviews-manager">
      <h2 className="font-bebas text-4xl tracking-wide text-cream">Avaliações dos clientes</h2>
      <p className="text-sm text-smoke mt-1">O cliente avalia pelo código na página de consulta, depois do atendimento. Destaque as melhores pra aparecerem no site.</p>

      {items.length === 0 ? (
        <div className="py-16 text-center" data-testid="reviews-empty">
          <MessageSquare className="w-8 h-8 text-gold/40 mx-auto mb-4" />
          <p className="text-sm text-smoke">Nenhuma avaliação ainda. Elas chegam quando um cliente avalia um horário que já passou.</p>
        </div>
      ) : (
        <div className="mt-8 space-y-3" data-testid="reviews-list">
          {items.map((r) => (
            <div
              key={r.id}
              data-testid={`review-row-${r.id}`}
              className={`border p-5 flex flex-wrap items-center gap-4 transition-colors ${r.featured ? "border-gold/50 bg-gold/5" : "border-white/10 bg-surface"}`}
            >
              <div className="flex-1 min-w-[220px]">
                <Stars n={r.rating} />
                <p className="font-bebas text-xl tracking-wide text-cream mt-2">{r.name}</p>
                <p className="text-xs text-smoke">{r.service_name} · com {r.barber_name} · {new Date(r.created_at).toLocaleDateString("pt-BR")}</p>
                {r.comment && <p className="text-sm text-cream/80 mt-2">"{r.comment}"</p>}
              </div>
              <button
                onClick={() => toggle(r.id, !r.featured)}
                data-testid={`review-feature-${r.id}`}
                className={`focus-gold font-bebas tracking-widest px-5 py-2 border transition-colors ${r.featured ? "bg-gold text-ink border-gold" : "border-white/15 text-smoke hover:border-gold hover:text-gold"}`}
              >
                {r.featured ? "Em destaque" : "Destacar"}
              </button>
              <button
                onClick={() => del(r.id)}
                aria-label="Remover avaliação"
                data-testid={`review-delete-${r.id}`}
                className="focus-gold p-2.5 border border-white/15 text-smoke hover:border-red-500 hover:text-red-400 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
